import React from 'react';
import { useChatStore } from '../../../store/chat_store';
import { useAuthStore } from '../../../store/authStore';

const ConversationList = ({ users, searchTerm }) => {
  const { conversations, activeConversation, setActiveConversation, onlineUsers } = useChatStore();
  const { user: currentUser } = useAuthStore();

  const handleSelect = (user, convId) => {
    setActiveConversation({
      otherUserId: user._id || user.id,
      otherUser: user,
      conversationId: convId
    });
  };

  const currentUserId = currentUser?._id || currentUser?.id;

  // Filter users: exclude self, then apply search
  const filteredUsers = (users || []).filter(u => {
    const uId = u._id || u.id;
    return uId !== currentUserId &&
      (u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
       u.email?.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  // Filter conversations: exclude self from participants, apply search
  const filteredConversations = conversations.filter(conv => {
    const otherUser = conv.participants.find(p => (p._id || p.id) !== currentUserId);
    if (!otherUser) return false;
    if (searchTerm) {
      return (
        otherUser.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        otherUser.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  // Users not yet in a conversation (for "All Users" section)
  const usersWithoutConversation = filteredUsers.filter(user => {
    const uId = user._id || user.id;
    return !conversations.some(c =>
      c.participants.some(p => (p._id || p.id) === uId)
    );
  });

  return (
    <div className="flex flex-col">

      {/* ── RECENT ── */}
      {filteredConversations.length > 0 && (
        <>
          <div className="px-4 pt-3 pb-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Recent</span>
          </div>
          {filteredConversations.map((conv) => {
            const otherUser = conv.participants.find(p => (p._id || p.id) !== currentUserId);
            if (!otherUser) return null;

            const otherUserId = otherUser._id || otherUser.id;
            const isActive = activeConversation?.otherUserId === otherUserId;
            const isOnline = onlineUsers[otherUserId]?.status === 'online';
            const unreadCount = conv.unreadCount || 0;

            return (
              <div
                key={conv._id}
                onClick={() => handleSelect(otherUser, conv._id)}
                className={`flex items-center p-3 mx-2 rounded-lg cursor-pointer transition-colors ${
                  isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold flex-shrink-0">
                    {otherUser.profile_image ? (
                      <img src={otherUser.profile_image} alt={otherUser.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      otherUser.name?.charAt(0).toUpperCase()
                    )}
                  </div>
                  {isOnline && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  )}
                </div>

                <div className="ml-3 flex-1 min-w-0">
                  <div className="flex justify-between items-baseline">
                    <h3 className={`text-sm font-medium truncate ${unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                      {otherUser.name}
                    </h3>
                    {conv.last_message && (
                      <span className="text-xs text-gray-400">
                        {new Date(conv.last_message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <p className={`text-sm truncate ${unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {conv.last_message?.content || 'Started a conversation'}
                    </p>
                    {unreadCount > 0 && (
                      <span className="ml-2 bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── ALL USERS ── always shown, excludes self & those already in Recent */}
      {usersWithoutConversation.length > 0 && (
        <>
          <div className="px-4 pt-3 pb-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">All Users</span>
          </div>
          {usersWithoutConversation.map(user => {
            const userId = user._id || user.id;
            const isOnline = onlineUsers[userId]?.status === 'online';
            const isActive = activeConversation?.otherUserId === userId;

            return (
              <div
                key={userId}
                onClick={() => handleSelect(user, null)}
                className={`flex items-center p-3 mx-2 rounded-lg cursor-pointer transition-colors ${
                  isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold flex-shrink-0">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  {isOnline && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                  )}
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-700 truncate">{user.name}</h3>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Empty state */}
      {filteredConversations.length === 0 && usersWithoutConversation.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-gray-400">No users found</div>
      )}

    </div>
  );
};

export default ConversationList;
